import React, { useState, useEffect } from 'react';
import styles from './slackSettings.module.css';
import { Form, Row, Col, Button, Select, Switch, message } from 'antd';
import { ConnectPrompt } from './ConnectPrompt';
import { get } from '../../utils/fetch';
import { useIntegrationConnection } from './useIntegrationConnection';

type OptionType = { value: string; label: string };

interface Props {
  projectId: string;
  organizationId: string;
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          channel_id: OptionType;
          new_tasks: boolean;
          new_comments: boolean;
          separate_comment_messages: boolean;
          status_updates: boolean;
        };
      }
    | undefined;
  onDisconnect: () => void;
  justUpgradedSlack?: boolean;
}

const SlackSettings: React.FC<Props> = (props: Props) => {
  const [form] = Form.useForm();
  const {
    integration,
    organizationId,
    projectId,
    onDisconnect,
    justUpgradedSlack
  } = props;

  const {
    isEnabled,
    getConnectedState,
    saveSettings,
    disconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration,
    integrationCode: 'Slack',
    onDisconnect
  });

  const [selectedChannel, setSelectedChannel] = useState(
    integration?.settings?.channel_id
  );
  const [newTasks, setNewTasks] = useState(
    integration?.settings?.new_tasks ?? false
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments ?? false
  );
  const [separateCommentMessages, setSeparateCommentMessages] = useState(
    integration?.settings?.separate_comment_messages ?? false
  );
  const [statusUpdates, setStatusUpdates] = useState(
    integration?.settings?.status_updates ?? false
  );

  const [remoteChannelOptions, setRemoteChannelOptions] = useState([]);
  const [isChannelsLoading, setIsChannelsLoading] = useState(false);

  const getChannels = async () => {
    if (!integration?.enabled) return;
    setIsChannelsLoading(true);
    try {
      // TODO: Ensure this endpoint exists or mock it
      const results = await get(
        `/organizations/${organizationId}/integrations/slack_channels?id=${integration.id}`
      );
      setRemoteChannelOptions(results);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch Slack channels. Please try again.');
    } finally {
      setIsChannelsLoading(false);
    }
  };

  useEffect(() => {
    if (integration?.enabled) {
      getConnectedState({ errorMessage: false });
      getChannels();
    }
  }, [integration, projectId, organizationId]);

  const renderSettings = () => (
    <Form
      form={form}
      layout="horizontal"
      onFinish={() =>
        saveSettings(
          {
            channel_id: selectedChannel,
            new_tasks: newTasks,
            new_comments: newComments,
            separate_comment_messages: separateCommentMessages,
            status_updates: statusUpdates
          },
          { form }
        )
      }
      requiredMark={false}
      className={styles.container}
    >
      <div className={styles.slackRequired}>
        <div className={styles.requiredBubble}>REQUIRED</div>
        <h3>Connect your Slack workspace</h3>
        <Row className={styles.slackEditRow}>
          <Col span={24}>
            <Form.Item
              name="channel_id"
              label={
                <span className={styles.requiredLabel}>
                  <span>*</span>Slack Channel
                </span>
              }
              className={styles.channelId}
              rules={[{ required: true, message: 'Please select a channel.' }]}
              initialValue={selectedChannel?.value}
            >
              <Select
                className={styles.slackSelect}
                showSearch
                placeholder="Select a channel"
                optionFilterProp="label"
                options={remoteChannelOptions}
                onChange={(_val, option: any) => setSelectedChannel(option)}
                loading={isChannelsLoading}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className={styles.slackWorkflows}>
        <h3>Choose your desired notifications</h3>
        <Row className={styles.slackEditRow}>
          <Col span={24}>
            <Form.Item
              label="New task notifications"
              className={styles.newTasks}
            >
              <Switch checked={newTasks} onChange={setNewTasks} />
            </Form.Item>
          </Col>
        </Row>
        <Row className={styles.slackEditRow}>
          <Col span={24}>
            <Form.Item
              label="New comment notifications"
              className={styles.newComments}
            >
              <Switch checked={newComments} onChange={setNewComments} />
            </Form.Item>
          </Col>
        </Row>
        <Row className={styles.slackEditRow}>
          <Col span={24}>
            <Form.Item
              label="Post each comment as a separate message"
              className={styles.separateCommentMessages}
            >
              <Switch
                checked={separateCommentMessages}
                onChange={setSeparateCommentMessages}
                disabled={!newComments}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row className={styles.slackEditRow}>
          <Col span={24}>
            <Form.Item
              label="Status change notifications"
              className={styles.statusUpdates}
            >
              <Switch checked={statusUpdates} onChange={setStatusUpdates} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <Row className={styles.slackEditRow}>
        <Col span={6}>
          <Form.Item>
            <Button
              type="primary"
              shape="round"
              htmlType="submit"
              className={styles.save}
            >
              Save
            </Button>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Button
            type="text"
            shape="round"
            onClick={() => disconnect()}
            className={styles.disconnectButton}
            danger
          >
            Disconnect
          </Button>
        </Col>
      </Row>
    </Form>
  );

  return (
    <div>
      {isEnabled ? (
        renderSettings()
      ) : (
        <ConnectPrompt
          integrationName="slack"
          projectId={projectId}
          organizationId={organizationId}
          type="Slack"
          display="Slack"
          provider="slack"
          showReconnectMessage={justUpgradedSlack}
        />
      )}
    </div>
  );
};

export default SlackSettings;
